import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import patientService from '../../../../services/patientService';
import careManagementService from '../../../../services/careManagementService';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEdit, faTrash, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { exportColoredTablePdf, sanitizeExportCellText } from '../../../../utils/coloredTablePdfExport';

import EmailModal from '../../../../components/EmailModal/EmailModal';
import AddFollowUpModal from '../../../../components/AddFollowUpModal/AddFollowUpModal';
import ListScreenSearchBar from '../../../../components/Common/ListScreenSearchBar/ListScreenSearchBar';
import ListScreenPaginationBar from '../../../../components/Common/ListScreenPaginationBar/ListScreenPaginationBar';
import AppDateInput from '../../../../components/Common/AppDateInput/AppDateInput';
import Table from '../../../../components/Common/Table/Table';
import { useBreadcrumb } from '../../../../contexts/BreadcrumbContext';
import { formatLocalDateShort, getTodayYmdLocal } from '../../../../utils/dateOnly';
import {
  formatFollowUpDateTime,
  getFollowUpServiceMinutes,
  getServiceTypeDisplay,
  resolveFollowUpClockTime,
  splitFollowUpServiceTimer,
} from '../../../../utils/followUpDisplayUtils';
import {
  DEFAULT_FOLLOW_UP_FILTERS,
  followUpListFiltersEqual,
  isFromToIncomplete,
  patchOnQueryModeChange,
} from '../../../../utils/queryPeriodFilters';
import { matchesWordStartAny } from '../../../../utils/searchMatch';
import { canEditFollowUpTime } from '../../../../utils/roleUtils';
import '../../../../components/Common/ListScreenPageLayout/listScreenPage.css';
import './FollowUpList.css';

const formatDateTime = (row) => formatFollowUpDateTime(row);

const formatDateShort = (dateString) => formatLocalDateShort(dateString);

const normalizeFollowUpStatusKey = (status) => {
    const normalized = String(status || '').toLowerCase().replace(/\s+/g, '_');
    if (!normalized) return 'continue_follow_up';
    if (['1', 'continue_follow_up', 'pending', 'in_progress'].includes(normalized)) {
        return 'continue_follow_up';
    }
    if (['0', 'no_need_to_follow_up', 'no_need', 'no_need_follow_up', 'completed', 'cancelled'].includes(normalized)) {
        return 'no_need_to_follow_up';
    }
    // Keep UI and filtering consistent: any non "no need" status is treated as continue.
    return 'continue_follow_up';
};

const renderFollowUpStatus = (status) => {
    return normalizeFollowUpStatusKey(status) === 'no_need_to_follow_up'
        ? 'No need to follow up'
        : 'Continue follow up';
};

const normalizeStatusForEdit = (status) => {
    const normalized = normalizeFollowUpStatusKey(status);
    return normalized === 'no_need_to_follow_up' ? 'no_need_to_follow_up' : 'continue_follow_up';
};

const toDateInputValue = (rawDate) => {
    if (!rawDate) return '';
    const d = new Date(rawDate);
    if (Number.isNaN(d.getTime())) return '';
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

const normalizeTimeInput = (raw) => {
    const value = String(raw || '').trim();
    if (!value) return '';
    if (/^\d{2}:\d{2}$/.test(value)) return value;
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return '';
    const hh = String(d.getHours()).padStart(2, '0');
    const mm = String(d.getMinutes()).padStart(2, '0');
    return `${hh}:${mm}`;
};

const splitTimerValue = (row) => splitFollowUpServiceTimer(row);

const FollowUpList = () => {
    const { practiceId, patientId } = useParams();
    const location = useLocation();
    const { updateBreadcrumbs } = useBreadcrumb();
    const { user } = useSelector((state) => state.auth || {});
    const canEditTime = canEditFollowUpTime(user);

    const initialCareProgram = String(location.state?.careProgram || 'RPM').toUpperCase();
    const [activeCareProgram, setActiveCareProgram] = useState(
        initialCareProgram === 'CCM' ? 'CCM' : initialCareProgram === 'PCM' ? 'PCM' : 'RPM'
    );

    const [patientData, setPatientData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [results, setResults] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortConfig, setSortConfig] = useState({ key: 'date_time', direction: 'desc' });

    const [showEmailModal, setShowEmailModal] = useState(false);
    const [emailSending, setEmailSending] = useState(false);
    const [showAddFollowUpModal, setShowAddFollowUpModal] = useState(false);
    const [showExportMenu, setShowExportMenu] = useState(false);
    const exportMenuRef = useRef(null);
    const fromDateRef = useRef(null);
    const toDateRef = useRef(null);
    const [entriesPerPage, setEntriesPerPage] = useState('10');
    const [currentPage, setCurrentPage] = useState(1);
    const [editingFollowUp, setEditingFollowUp] = useState(null);
    const [editForm, setEditForm] = useState({
        content: '',
        follow_up_status: '',
        service_type: '',
        assign_to: '',
        date: '',
        time: '',
        serviceMinutes: '0',
        serviceSeconds: '00'
    });

    // Draft filters (edited in the form) vs applied filters (used by the table after Query).
    // These are never auto-cleared on program switch or refetch — only Query / Reset change applied state.
    const [filters, setFilters] = useState({ ...DEFAULT_FOLLOW_UP_FILTERS });
    const [appliedFilters, setAppliedFilters] = useState({ ...DEFAULT_FOLLOW_UP_FILTERS });
    const [queryLoading, setQueryLoading] = useState(false);

    const fromToIncomplete = isFromToIncomplete(filters.dateRangeType, filters.fromDate, filters.toDate);
    const isQueryDisabled = loading || queryLoading || fromToIncomplete;

    // Reset is enabled when draft or applied filters differ from defaults
    const isFiltersDirty = !followUpListFiltersEqual(filters, DEFAULT_FOLLOW_UP_FILTERS)
        || !followUpListFiltersEqual(appliedFilters, DEFAULT_FOLLOW_UP_FILTERS);

    const isResetDisabled = loading || queryLoading || !isFiltersDirty;

    const fetchPatientInfo = async () => {
        try {
            const response = await patientService.getPatientDetails(practiceId, patientId);
            if (response.data?.success) {
                setPatientData(response.data.data.patient);
            }
        } catch (error) {
            console.error('Error fetching patient info:', error);
        }
    };

    const fetchFollowUps = async () => {
        try {
            setLoading(true);
            const response = await patientService.getFollowUps(practiceId, patientId, activeCareProgram);
            if (response.data?.success) {
                const enriched = (response.data.data || []).map((item, index) => ({
                    ...item,
                    originalIndex: index + 1
                }));
                setResults(enriched);
            } else {
                setResults([]);
            }
        } catch (error) {
            console.error('Error fetching follow-ups:', error);
            setResults([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const fromState = String(location.state?.careProgram || '').toUpperCase();
        if (fromState === 'RPM' || fromState === 'CCM' || fromState === 'PCM') {
            setActiveCareProgram(fromState);
        }
    }, [location.state?.careProgram]);

    useEffect(() => {
        fetchPatientInfo();
        fetchFollowUps();
    }, [practiceId, patientId, activeCareProgram]);

    const handleFilterChange = (field, value) => {
        setFilters(prev => {
            if (field === 'dateRangeType') {
                return {
                    ...prev,
                    ...patchOnQueryModeChange(value, { typeKey: 'dateRangeType' }),
                };
            }
            const next = { ...prev, [field]: value };
            // If the user picks a From date that is after the current To date,
            // clear To so the user re-selects a valid (>= From) value.
            if (field === 'fromDate' && value && next.toDate && next.toDate < value) {
                next.toDate = '';
            }
            return next;
        });
    };

    const handleQuery = () => {
        if (fromToIncomplete) return;
        setQueryLoading(true);
        setAppliedFilters({ ...filters });
        setCurrentPage(1);
        // Brief loader so Query feels consistent with other list screens
        window.setTimeout(() => setQueryLoading(false), 200);
    };

    const handleReset = () => {
        if (isResetDisabled) return;
        const reset = { ...DEFAULT_FOLLOW_UP_FILTERS };
        setFilters(reset);
        setAppliedFilters(reset);
        setCurrentPage(1);
        setQueryLoading(true);
        window.setTimeout(() => setQueryLoading(false), 200);
    };

    const handleSort = (key) => {
        let direction = 'asc';
        if (sortConfig.key === key && sortConfig.direction === 'asc') {
            direction = 'desc';
        }
        setSortConfig({ key, direction });
    };

    const filteredData = useMemo(() => {
        let filtered = [...results];

        const now = new Date();
        const rowDate = (row) => new Date(row.date || row.created_at || row.date_time);

        if (appliedFilters.dateRangeType === 'dateRange' && appliedFilters.dateRange) {
            const value = appliedFilters.dateRange;
            filtered = filtered.filter((row) => {
                const d = rowDate(row);
                if (Number.isNaN(d.getTime())) return false;
                if (value === 'Last 7 days') {
                    const from = new Date(now);
                    from.setDate(now.getDate() - 7);
                    return d >= from && d <= now;
                }
                if (value === 'Last 30 days') {
                    const from = new Date(now);
                    from.setDate(now.getDate() - 30);
                    return d >= from && d <= now;
                }
                if (value === 'Last 90 days') {
                    const from = new Date(now);
                    from.setDate(now.getDate() - 90);
                    return d >= from && d <= now;
                }
                if (value === 'Last 14 days') {
                    const from = new Date(now);
                    from.setDate(now.getDate() - 14);
                    return d >= from && d <= now;
                }
                if (value === 'All') {
                    return true;
                }
                return true;
            });
        }

        if (appliedFilters.dateRangeType === 'fromTo' && (appliedFilters.fromDate || appliedFilters.toDate)) {
            const from = appliedFilters.fromDate ? new Date(appliedFilters.fromDate) : null;
            const to = appliedFilters.toDate ? new Date(appliedFilters.toDate) : null;
            if (to) to.setHours(23, 59, 59, 999);
            filtered = filtered.filter((row) => {
                const d = rowDate(row);
                if (Number.isNaN(d.getTime())) return false;
                if (from && d < from) return false;
                if (to && d > to) return false;
                return true;
            });
        }

        if (appliedFilters.followUpStatus) {
            filtered = filtered.filter(row => {
                return normalizeFollowUpStatusKey(row.follow_up_status) === normalizeFollowUpStatusKey(appliedFilters.followUpStatus);
            });
        }

        if (appliedFilters.serviceTime) {
            const minutesFromRow = (row) => Number.parseInt(row.service_time || row.timerValue || 0, 10) || 0;
            filtered = filtered.filter((row) => {
                const minutes = minutesFromRow(row);
                if (appliedFilters.serviceTime === 'All Data') return true;
                if (appliedFilters.serviceTime === 'Less than 20 minutes') return minutes < 20;
                if (appliedFilters.serviceTime === '99470- Greater than or equal to 10 minutes but less than 20 minutes') return minutes >= 10 && minutes < 20;
                if (appliedFilters.serviceTime === '99457- Greater than 20 minutes but less than 40 minutes') return minutes >= 20 && minutes < 40;
                if (appliedFilters.serviceTime === '99458- greater than 40 minutes but less than 60 minutes') return minutes >= 40 && minutes < 60;
                if (appliedFilters.serviceTime === '99458- greater than 60 minutes') return minutes >= 60;
                return true;
            });
        }

        if (searchTerm) {
            filtered = filtered.filter((row) => {
                const rowTokens = [
                    String(row.originalIndex ?? ''),
                    formatDateTime(row),
                    String(row.caregiver_name || row.caregiver_provider || ''),
                    String(getFollowUpServiceMinutes(row)),
                    String(row.fu_note_content || row.content || ''),
                    getServiceTypeDisplay(row.service_type),
                    renderFollowUpStatus(row.follow_up_status)
                ];
                return matchesWordStartAny(rowTokens, searchTerm);
            });
        }

        // Sorting logic
        if (sortConfig.key) {
            filtered.sort((a, b) => {
                let aVal, bVal;

                if (sortConfig.key === 'index') {
                    aVal = Number.parseInt(a.originalIndex ?? 0, 10) || 0;
                    bVal = Number.parseInt(b.originalIndex ?? 0, 10) || 0;
                } else if (sortConfig.key === 'date_time') {
                    // Combine date and time for accurate sorting
                    const parseDate = (item) => {
                        const dStr = item.date ? item.date.split('T')[0] : (item.created_at ? item.created_at.split('T')[0] : '');
                        const tStr = resolveFollowUpClockTime(item);
                        if (!dStr) return 0;
                        // Robust parsing: YYYY-MM-DD HH:mm:ss
                        const d = new Date(`${dStr}T${tStr.includes(':') ? tStr : tStr + ':00'}`);
                        return isNaN(d.getTime()) ? new Date(dStr).getTime() || 0 : d.getTime();
                    };
                    aVal = parseDate(a);
                    bVal = parseDate(b);
                } else if (sortConfig.key === 'caregiver') {
                    aVal = (a.caregiver_name || a.caregiver_provider || '').toLowerCase().trim();
                    bVal = (b.caregiver_name || b.caregiver_provider || '').toLowerCase().trim();
                } else if (sortConfig.key === 'service_time') {
                    aVal = Number.parseInt(a.service_time || a.timerValue || 0, 10) || 0;
                    bVal = Number.parseInt(b.service_time || b.timerValue || 0, 10) || 0;
                } else if (sortConfig.key === 'notes') {
                    aVal = (a.fu_note_content || a.content || '').toLowerCase().trim();
                    bVal = (b.fu_note_content || b.content || '').toLowerCase().trim();
                } else if (sortConfig.key === 'service_type') {
                    aVal = getServiceTypeDisplay(a.service_type).toLowerCase().trim();
                    bVal = getServiceTypeDisplay(b.service_type).toLowerCase().trim();
                } else if (sortConfig.key === 'status') {
                    aVal = renderFollowUpStatus(a.follow_up_status).toLowerCase().trim();
                    bVal = renderFollowUpStatus(b.follow_up_status).toLowerCase().trim();
                }

                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
            });
        }

        return filtered;
    }, [results, appliedFilters, searchTerm, sortConfig]);

    const pageSize = entriesPerPage === 'all' ? Math.max(filteredData.length, 1) : parseInt(entriesPerPage, 10);
    const totalPages = Math.max(1, Math.ceil(filteredData.length / pageSize));
    const paginatedData = filteredData.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    useEffect(() => {
        if (currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [currentPage, totalPages]);

    const patientName = patientData
        ? `${patientData.last_name || ''}, ${patientData.first_name || ''}`.trim()
        : '...';

    const dob = patientData?.date_of_birth ? formatDateShort(patientData.date_of_birth) : '...';
    const practiceName = patientData?.practice_name || patientData?.practice || 'Practice';

    useEffect(() => {
        if (!patientData) return;
        const fromDashboard = location.state?.fromDashboard;
        const rootCrumb = { label: 'Dashboard', path: '/dashboard' };

        const patientLabel = patientName && patientName !== '...' ? patientName : 'Patient';
        updateBreadcrumbs([
            rootCrumb,
            { label: practiceName, path: `/practices/${practiceId}`, state: { fromDashboard } },
            { label: 'Patients', path: `/practices/${practiceId}?tab=patient`, state: { fromDashboard } },

            { label: patientLabel, path: `/practices/${practiceId}/patients/${patientId}`, state: { fromDashboard } },
            { label: 'Follow Up List', path: null, active: true }
        ]);
    }, [patientData, patientName, practiceId, patientId, practiceName, updateBreadcrumbs, location.state]);
    useEffect(() => {
        if (!showExportMenu) return undefined;
        const onDocClick = (e) => {
            if (exportMenuRef.current && !exportMenuRef.current.contains(e.target)) {
                setShowExportMenu(false);
            }
        };
        document.addEventListener('click', onDocClick);
        return () => document.removeEventListener('click', onDocClick);
    }, [showExportMenu]);

    // --- Actions Implementations --- //

    const handlePrint = () => {
        if (filteredData.length === 0) {
            toast.warning('No data to print');
            return;
        }

        const printWindow = window.open('', '_blank');
        if (!printWindow) {
            toast.error('Please allow popups to print');
            return;
        }

        const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Follow Up List Report - ${patientName}</title>
          <style>
            @media print {
              @page {
                margin: 0.5in;
                size: letter landscape;
              }
            }
            body {
              font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
              font-size: 11px;
              color: #1e293b;
              margin: 0;
              padding: 20px;
              background: #fff;
            }
            .print-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              border-bottom: 2px solid #0077b6;
              padding-bottom: 12px;
              margin-bottom: 16px;
            }
            .print-title {
              font-size: 20px;
              font-weight: 700;
              color: #0077b6;
              margin: 0;
            }
            .print-subtitle {
              font-size: 12px;
              color: #64748b;
              margin-top: 4px;
            }
            .meta-bar {
              display: flex;
              justify-content: space-between;
              background: #f8fafc;
              border: 1px solid #e2e8f0;
              border-radius: 6px;
              padding: 10px 16px;
              margin-bottom: 16px;
              font-size: 12px;
            }
            .meta-item { font-weight: 600; color: #334155; }
            .meta-item span { font-weight: 400; color: #64748b; }
            table {
              width: 100%;
              border-collapse: collapse;
              font-size: 10px;
              table-layout: fixed;
            }
            th, td {
              border: 1px solid #cbd5e1;
              padding: 6px 8px;
              text-align: left;
              vertical-align: middle;
              word-wrap: break-word;
            }
            th {
              background-color: #0077b6;
              color: #ffffff;
              font-weight: 600;
              text-transform: uppercase;
              font-size: 9px;
              letter-spacing: 0.05em;
            }
            tr:nth-child(even) { background-color: #f8fafc; }
            .print-footer {
              margin-top: 16px;
              text-align: right;
              font-size: 10px;
              color: #64748b;
            }
          </style>
        </head>
        <body>
          <div class="print-header">
            <div>
              <h1 class="print-title">EVitals - Follow Up List</h1>
              <div class="print-subtitle">Care Program: ${activeCareProgram}</div>
            </div>
            <div style="text-align: right; font-size: 11px; color: #64748b;">
              Generated: ${new Date().toLocaleString()}
            </div>
          </div>
          <div class="meta-bar">
            <div class="meta-item">Patient: <span>${patientName}</span></div>
            <div class="meta-item">DOB: <span>${dob}</span></div>
            <div class="meta-item">Practice: <span>${practiceName}</span></div>
            <div class="meta-item">Total Records: <span>${filteredData.length}</span></div>
          </div>
          <table>
            <thead>
              <tr>
                <th style="width: 4%;">#</th>
                <th style="width: 14%;">Date &amp; Time</th>
                <th style="width: 15%;">Caregiver</th>
                <th style="width: 10%;">Time (Min)</th>
                <th style="width: 35%;">Follow Up Notes</th>
                <th style="width: 10%;">Service Type</th>
                <th style="width: 12%;">Status</th>
              </tr>
            </thead>
            <tbody>
              ${filteredData.map((result, idx) => `
                <tr>
                  <td style="text-align: center;">${idx + 1}</td>
                  <td>${formatDateTime(result)}</td>
                  <td>${result.caregiver_name || result.caregiver_provider || 'N/A'}</td>
                  <td style="text-align: center;">${getFollowUpServiceMinutes(result)}</td>
                  <td>${sanitizeExportCellText(result.fu_note_content || result.content || 'N/A').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/\n/g, '<br>')}</td>
                  <td>${getServiceTypeDisplay(result.service_type)}</td>
                  <td>${renderFollowUpStatus(result.follow_up_status)}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
          <div class="print-footer">
            Report generated from EVitals Platform
          </div>
        </body>
      </html>
    `;

        printWindow.document.write(printContent);
        printWindow.document.close();
        printWindow.focus();

        setTimeout(() => {
            printWindow.print();
            printWindow.close();
        }, 250);
    };

    const handleExportPDF = () => {
        if (filteredData.length === 0) {
            toast.warning('No data to export');
            return;
        }

        try {
            exportColoredTablePdf({
                filename: `Follow-Up-List-${patientName.replace(/\s+/g, '-')}-${getTodayYmdLocal()}.pdf`,
                title: `Follow Up List - ${activeCareProgram}`,
                metaLines: [
                    `Patient: ${patientName} | DOB: ${dob}`,
                    `Practice: ${practiceName}`,
                    `Generated: ${new Date().toLocaleString()}`,
                    `Total Records: ${filteredData.length}`,
                ],
                headers: ['#', 'Date & Time', 'Caregiver', 'Service Time (Min)', 'Follow Up Notes', 'Service Type', 'Status'],
                rows: filteredData.map((result, idx) => [
                    idx + 1,
                    formatDateTime(result),
                    result.caregiver_name || result.caregiver_provider || 'N/A',
                    getFollowUpServiceMinutes(result),
                    sanitizeExportCellText(result.fu_note_content || result.content || 'N/A'),
                    getServiceTypeDisplay(result.service_type),
                    renderFollowUpStatus(result.follow_up_status),
                ]),
                headerBg: '#0077b6',
                columnStyles: {
                    0: { cellWidth: 24, halign: 'center' },
                    1: { cellWidth: 95 },
                    2: { cellWidth: 95 },
                    3: { cellWidth: 60, halign: 'center' },
                    4: { cellWidth: 'auto' },
                    5: { cellWidth: 80 },
                    6: { cellWidth: 80 },
                },
                fontSize: 7.5,
            });
            toast.success('Report exported to PDF successfully');
            patientService.logPatientExport(practiceId, patientId, { format: 'pdf', source: 'Follow-up' });
        } catch (error) {
            console.error('Error exporting PDF:', error);
            toast.error('Failed to export PDF');
        }
    };

    const handleExportCSV = () => {
        if (filteredData.length === 0) {
            toast.warning('No data to export');
            return;
        }

        try {
            const headers = ['#', 'Date & Time', 'Caregiver', 'Service Time (Min)', 'Follow Up Notes', 'Service Type', 'Follow Up Status'];
            const rows = filteredData.map((result, idx) => [
                idx + 1,
                formatDateTime(result),
                result.caregiver_name || result.caregiver_provider || 'N/A',
                getFollowUpServiceMinutes(result),
                sanitizeExportCellText(result.fu_note_content || result.content || 'N/A'),
                getServiceTypeDisplay(result.service_type),
                renderFollowUpStatus(result.follow_up_status),
            ]);

            const csvRows = [
                headers.map((h) => `"${h.replace(/"/g, '""')}"`).join(','),
                ...rows.map((row) =>
                    row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(',')
                ),
            ];

            const blob = new Blob([csvRows.join('\n')], { type: 'text/csv;charset=utf-8;' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute(
                'download',
                `Follow-Up-List-${patientName.replace(/\s+/g, '-')}-${getTodayYmdLocal()}.csv`
            );
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
            toast.success('Report exported to CSV successfully');
            patientService.logPatientExport(practiceId, patientId, { format: 'csv', source: 'Follow-up' });
        } catch (error) {
            console.error('Error exporting CSV:', error);
            toast.error('Failed to export CSV');
        }
    };

    const generateEmailContent = (data) => {
        if (!data || data.length === 0) return 'No follow-up data available.';
        const reportText = data.map((result, idx) => {
            return `${idx + 1}. Date: ${formatDateTime(result)}\n   Caregiver: ${result.caregiver_name || result.caregiver_provider || 'N/A'}\n   Time: ${getFollowUpServiceMinutes(result)} min\n   Status: ${renderFollowUpStatus(result.follow_up_status)}\n   Note: ${sanitizeExportCellText(result.fu_note_content || result.content || 'N/A').substring(0, 200)}...\n`;
        }).join('\n');
        return `Follow Up List Report\nPatient: ${patientName} (DOB: ${dob})\nGenerated: ${new Date().toLocaleString()}\nTotal Records: ${data.length}\n\n${reportText}`;
    };

    const handleSendEmail = async (emailData) => {
        try {
            setEmailSending(true);
            const response = await careManagementService.emailFollowUpQuery(
                { practiceId, patientId, careProgram: activeCareProgram },
                emailData
            );

            if (response.data?.success || response.status === 200) {
                toast.success('Email sent successfully');
                patientService.logPatientExport(practiceId, patientId, {
                    format: 'email',
                    source: 'Follow-up'
                });
                setShowEmailModal(false);
            } else {
                toast.error(response.data?.message || 'Failed to send email');
            }
        } catch (error) {
            console.error('Error sending email:', error);
            toast.error(error.response?.data?.message || 'Failed to send email');
        } finally {
            setEmailSending(false);
        }
    };

    const openDatePicker = (ref) => {
        ref?.current?.setOpen?.(true);
    };

    const handleEditFollowUp = async (row) => {
        const followUpId = row?.id;
        if (!followUpId) {
            toast.error('Follow-up ID not found');
            return;
        }
        const timerParts = splitTimerValue(row);
        setEditingFollowUp(row);
        setEditForm({
            content: row?.fu_note_content || row?.content || '',
            follow_up_status: normalizeStatusForEdit(row?.follow_up_status),
            service_type: getServiceTypeDisplay(row?.service_type),
            assign_to: row?.caregiver_name || row?.caregiver_provider || 'N/A',
            date: toDateInputValue(row?.date || row?.created_at || row?.date_time),
            time: resolveFollowUpClockTime(row),
            serviceMinutes: timerParts.minutes,
            serviceSeconds: timerParts.seconds
        });
    };

    const handleDeleteFollowUp = async (row) => {
        const followUpId = row?.id;
        if (!followUpId) {
            toast.error('Follow-up ID not found');
            return;
        }
        if (!window.confirm('Are you sure you want to delete this follow-up note?')) return;

        try {
            const response = await patientService.deleteFollowUp(practiceId, patientId, followUpId);
            if (response.data?.success) {
                toast.success('Follow-up deleted successfully');
                fetchFollowUps();
            } else {
                toast.error(response.data?.message || 'Failed to delete follow-up');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to delete follow-up');
        }
    };

    const handleSaveEdit = async () => {
        if (!editingFollowUp?.id) return;
        if (!editForm.content.trim()) {
            toast.error('Follow-up note cannot be empty');
            return;
        }
        const safeMinutes = Math.max(0, Number.parseInt(editForm.serviceMinutes || '0', 10) || 0);
        const safeSeconds = Math.min(59, Math.max(0, Number.parseInt(editForm.serviceSeconds || '0', 10) || 0));
        if (canEditTime && safeMinutes < 1) {
            toast.error('Follow-up minutes cannot be 0');
            return;
        }
        const timerValue = `${String(safeMinutes).padStart(2, '0')}:${String(safeSeconds).padStart(2, '0')}`;
        const payload = {
                content: editForm.content.trim(),
                follow_up_status: editForm.follow_up_status,
                date: editForm.date,
                time: editForm.time,
        };
        if (canEditTime) {
            payload.timerValue = timerValue;
        }
        try {
            const response = await patientService.updateFollowUp(practiceId, patientId, editingFollowUp.id, payload);
            if (response.data?.success) {
                toast.success('Follow-up updated successfully');
                setEditingFollowUp(null);
                fetchFollowUps();
            } else {
                toast.error(response.data?.message || 'Failed to update follow-up');
            }
        } catch (error) {
            toast.error(error.response?.data?.message || 'Failed to update follow-up');
        }
    };

    return (
        <div className="list-screen-page-container follow-up-list-page">
            <div className="list-screen-top-bar follow-up-list-header">
                <div className="list-screen-title-section follow-up-list-title-area">
                    <h2 className="dashboard-title list-screen-title-text follow-up-list-title">
                        Follow Up List · {activeCareProgram}
                    </h2>
                    <div className="follow-up-program-tabs">
                        {['RPM', 'CCM', 'PCM'].map((program) => (
                            <button
                                key={program}
                                type="button"
                                className={`follow-up-program-tab ${activeCareProgram === program ? 'active' : ''}`}
                                onClick={() => setActiveCareProgram(program)}
                            >
                                {program}
                            </button>
                        ))}
                    </div>
                </div>
                <div className="list-screen-actions follow-up-list-header-actions">
                    <div className="patient-info-block">
                        <div className="patient-info-row">
                            <span className="patient-info-label">Name</span>
                            <span className="patient-info-colon">:</span>
                            <span className="patient-info-value">{patientName}</span>
                        </div>
                        <div className="patient-info-row">
                            <span className="patient-info-label">DOB</span>
                            <span className="patient-info-colon">:</span>
                            <span className="patient-info-value">{dob}</span>
                        </div>
                    </div>
                </div>
            </div>

            <div className="query-period-card">
                <div className="query-period-title">Query Period</div>

                <div className="filter-grid-layout">
                    {/* Left Column */}
                    <div className="filter-left-col">
                        <div className="date-row-filter">
                            <label className="radio-label radio-only">
                                <input
                                    type="radio"
                                    name="dateRangeType"
                                    checked={filters.dateRangeType === 'dateRange'}
                                    onChange={() => handleFilterChange('dateRangeType', 'dateRange')}
                                />
                            </label>
                            <div className="date-input-group">
                                <label>Date Range</label>
                                <select
                                    className="form-select-fl"
                                    value={filters.dateRange}
                                    onChange={(e) => handleFilterChange('dateRange', e.target.value)}
                                    disabled={filters.dateRangeType !== 'dateRange'}
                                >
                                    <option value="Last 7 days">Last 7 days</option>
                                    <option value="Last 14 days">Last 14 days</option>
                                    <option value="Last 30 days">Last 30 days</option>
                                    <option value="Last 90 days">Last 90 days</option>
                                    <option value="All">All</option>
                                </select>
                            </div>
                            <span />
                        </div>

                        <div className="date-row-filter">
                            <label className="radio-label radio-only">
                                <input
                                    type="radio"
                                    name="dateRangeType"
                                    checked={filters.dateRangeType === 'fromTo'}
                                    onChange={() => handleFilterChange('dateRangeType', 'fromTo')}
                                />
                            </label>
                            <div className="date-input-group">
                                <label>From</label>
                                <AppDateInput
                                    ref={fromDateRef}
                                    className="form-input-fl"
                                    value={filters.fromDate}
                                    max={filters.toDate || undefined}
                                    onChange={(v) => handleFilterChange('fromDate', v)}
                                    disabled={filters.dateRangeType !== 'fromTo'}
                                />
                            </div>
                            <div className="date-input-group">
                                <label>To</label>
                                <AppDateInput
                                    ref={toDateRef}
                                    className="form-input-fl"
                                    value={filters.toDate}
                                    min={filters.fromDate || undefined}
                                    onChange={(v) => handleFilterChange('toDate', v)}
                                    disabled={filters.dateRangeType !== 'fromTo' || !filters.fromDate}
                                    title={!filters.fromDate ? 'Select a From date first' : ''}
                                />
                            </div>
                        </div>
                    </div>

                    {/* Right Column */}
                    <div className="filter-right-col">
                        <div className="form-field-filter">
                            <label>Service Time</label>
                            <div style={{ position: 'relative' }}>
                                <select
                                    className="form-select-fl"
                                    value={filters.serviceTime}
                                    onChange={(e) => handleFilterChange('serviceTime', e.target.value)}
                                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '12px 12px', appearance: 'none' }}
                                >
                                    <option value="">Select Service Time</option>
                                    <option value="All Data">All Data</option>
                                    <option value="Less than 20 minutes">Less than 20 minutes</option>
                                    <option value="99470- Greater than or equal to 10 minutes but less than 20 minutes">99470- Greater than or equal to 10 minutes but less than 20 minutes</option>
                                    <option value="99457- Greater than 20 minutes but less than 40 minutes">99457- Greater than 20 minutes but less than 40 minutes</option>
                                    <option value="99458- greater than 40 minutes but less than 60 minutes">99458- greater than 40 minutes but less than 60 minutes</option>
                                    <option value="99458- greater than 60 minutes">99458- greater than 60 minutes</option>
                                </select>
                            </div>
                        </div>
                        <div className="form-field-filter follow-up-status-filter">
                            <label>Follow Up Status</label>
                            <div style={{ position: 'relative' }}>
                                <select
                                    className="form-select-fl"
                                    value={filters.followUpStatus}
                                    onChange={(e) => handleFilterChange('followUpStatus', e.target.value)}
                                    style={{ backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23333' d='M6 9L1 4h10z'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat', backgroundPosition: 'right 12px center', backgroundSize: '12px 12px', appearance: 'none' }}
                                >
                                    <option value="">Select Follow Up Status</option>
                                    <option value="no_need_to_follow_up">No need to follow up</option>
                                    <option value="continue_follow_up">Continue follow up</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="form-actions-fl">
                    <button type="button" className="btn-fl" onClick={handleQuery} disabled={isQueryDisabled}>Query</button>
                    <button
                        type="button"
                        className="btn-fl btn-fl-reset"
                        onClick={handleReset}
                        disabled={isResetDisabled}
                    >
                        Reset
                    </button>
                    <button type="button" className="btn-fl" onClick={() => setShowAddFollowUpModal(true)}>Add Data</button>
                    <button type="button" className="btn-fl" onClick={handlePrint} disabled={loading || filteredData.length === 0}>Print</button>
                    <div className="export-dropdown-wrapper" ref={exportMenuRef} style={{ position: 'relative', display: 'inline-block' }}>
                        <button
                            type="button"
                            className="btn-fl"
                            onClick={() => setShowExportMenu((prev) => !prev)}
                            disabled={loading || filteredData.length === 0}
                        >
                            Export <FontAwesomeIcon icon={faChevronDown} style={{ marginLeft: 4, fontSize: 11 }} />
                        </button>
                        {showExportMenu && (
                            <div
                                className="export-dropdown-menu"
                                style={{
                                    position: 'absolute',
                                    top: '100%',
                                    right: 0,
                                    marginTop: 4,
                                    background: '#fff',
                                    border: '1px solid #cbd5e1',
                                    borderRadius: 6,
                                    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                                    zIndex: 100,
                                    minWidth: 120,
                                    padding: '4px 0',
                                }}
                            >
                                <button
                                    type="button"
                                    style={{
                                        display: 'block',
                                        width: '100%',
                                        padding: '8px 16px',
                                        textAlign: 'left',
                                        background: 'none',
                                        border: 'none',
                                        fontSize: 13,
                                        cursor: 'pointer',
                                        color: '#334155',
                                    }}
                                    onClick={() => {
                                        setShowExportMenu(false);
                                        handleExportPDF();
                                    }}
                                >
                                    Export PDF
                                </button>
                                <button
                                    type="button"
                                    style={{
                                        display: 'block',
                                        width: '100%',
                                        padding: '8px 16px',
                                        textAlign: 'left',
                                        background: 'none',
                                        border: 'none',
                                        fontSize: 13,
                                        cursor: 'pointer',
                                        color: '#334155',
                                    }}
                                    onClick={() => {
                                        setShowExportMenu(false);
                                        handleExportCSV();
                                    }}
                                >
                                    Export CSV
                                </button>
                            </div>
                        )}
                    </div>
                    <button type="button" className="btn-fl" onClick={() => setShowEmailModal(true)} disabled={loading || filteredData.length === 0}>Email</button>
                </div>
            </div>

            <div className="follow-up-list-table-section">
                <div className="table-toolbar-fl">
                    <ListScreenSearchBar
                        value={searchTerm}
                        onChange={setSearchTerm}
                        inputId="follow-up-list-search"
                    />
                </div>

                <div className="table-wrap-fl">
                    <Table
                        columns={[
                            {
                                key: 'index',
                                title: '#',
                                sortable: true,
                                className: 'reusable-table-centered-col',
                                render: (_row, idx) => ((currentPage - 1) * pageSize) + idx + 1,
                            },
                            {
                                key: 'date_time',
                                title: 'Date & Time',
                                sortable: true,
                                render: (row) => formatDateTime(row),
                            },
                            {
                                key: 'caregiver',
                                title: 'Caregiver',
                                sortable: true,
                                render: (row) => row.caregiver_name || row.caregiver_provider || 'N/A',
                            },
                            {
                                key: 'service_time',
                                title: 'Service Time (Min)',
                                sortable: true,
                                className: 'reusable-table-centered-col',
                                render: (row) => getFollowUpServiceMinutes(row),
                            },
                            {
                                key: 'notes',
                                title: 'Follow Up Notes',
                                sortable: true,
                                render: (row) => (
                                    <div className="follow-up-notes-cell">
                                        {row.fu_note_content || row.content || 'N/A'}
                                    </div>
                                ),
                            },
                            {
                                key: 'service_type',
                                title: 'Service Type',
                                sortable: true,
                                render: (row) => getServiceTypeDisplay(row.service_type),
                            },
                            {
                                key: 'status',
                                title: 'Follow Up Status',
                                sortable: true,
                                render: (row) => renderFollowUpStatus(row.follow_up_status),
                            },
                            {
                                key: 'action',
                                title: 'Action',
                                className: 'reusable-table-centered-col',
                                render: (row) => (
                                    <div className="action-icons-fl">
                                        <span
                                            className="action-icon-fl edit"
                                            onClick={() => handleEditFollowUp(row)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') handleEditFollowUp(row);
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faEdit} />
                                        </span>
                                        <span
                                            className="action-icon-fl delete"
                                            onClick={() => handleDeleteFollowUp(row)}
                                            role="button"
                                            tabIndex={0}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') handleDeleteFollowUp(row);
                                            }}
                                        >
                                            <FontAwesomeIcon icon={faTrash} />
                                        </span>
                                    </div>
                                ),
                            },
                        ]}
                        data={paginatedData}
                        sortBy={sortConfig.key}
                        sortOrder={sortConfig.direction}
                        onSort={handleSort}
                        isLoading={loading || queryLoading}
                        loadingMode="skeleton"
                        skeletonRows={8}
                        emptyMessage="No follow ups found."
                        getRowKey={(row, idx) => row.id || row.follow_up_id || `fu-${idx}`}
                    />
                </div>

                <ListScreenPaginationBar
                    entriesValue={entriesPerPage}
                    onEntriesChange={(value) => {
                        setEntriesPerPage(value);
                        setCurrentPage(1);
                    }}
                    currentPage={currentPage}
                    totalPages={totalPages}
                    onPageChange={setCurrentPage}
                />
            </div>

            {/* Modals */}
            <EmailModal
                isOpen={showEmailModal}
                onClose={() => setShowEmailModal(false)}
                onSend={handleSendEmail}
                defaultSubject={`Follow Up List Report - ${new Date().toLocaleDateString()}`}
                defaultContent={generateEmailContent(filteredData)}
            />

            <AddFollowUpModal
                isOpen={showAddFollowUpModal}
                onClose={() => setShowAddFollowUpModal(false)}
                practiceId={practiceId}
                patientId={patientId}
                patientName={patientName}
                careProgram={activeCareProgram}
                onSuccess={() => {
                    setShowAddFollowUpModal(false);
                    fetchFollowUps();
                }}
            />

            {editingFollowUp && (
                <div className="follow-up-edit-overlay" onClick={() => setEditingFollowUp(null)}>
                    <div className="follow-up-edit-card" onClick={(e) => e.stopPropagation()}>
                        <h4>Edit Follow Up</h4>
                        <div className="follow-up-edit-grid">
                            <div className="follow-up-edit-field">
                                <label>Service Type</label>
                                <input className="form-input-fl" value={editForm.service_type} disabled />
                            </div>
                            <div className="follow-up-edit-field">
                                <label>Follow Up Status</label>
                                <input className="form-input-fl" value={renderFollowUpStatus(editForm.follow_up_status)} disabled />
                            </div>
                            <div className="follow-up-edit-field">
                                <label>Assign To</label>
                                <input className="form-input-fl" value={editForm.assign_to} disabled />
                            </div>
                            <div className="follow-up-edit-field">
                                <label>Service Time (MM:SS)</label>
                                <div className="follow-up-edit-timer-row">
                                    <input
                                        className="form-input-fl"
                                        type="number"
                                        min="0"
                                        value={editForm.serviceMinutes}
                                        disabled={!canEditTime}
                                        title={!canEditTime ? 'Only Super Admin, Practice Admin, and Provider can edit service time' : undefined}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, serviceMinutes: e.target.value }))}
                                    />
                                    <span>:</span>
                                    <input
                                        className="form-input-fl"
                                        type="number"
                                        min="0"
                                        max="59"
                                        value={editForm.serviceSeconds}
                                        disabled={!canEditTime}
                                        title={!canEditTime ? 'Only Super Admin, Practice Admin, and Provider can edit service time' : undefined}
                                        onChange={(e) => setEditForm((prev) => ({ ...prev, serviceSeconds: e.target.value }))}
                                    />
                                </div>
                                {!canEditTime && (
                                    <span className="follow-up-edit-time-hint">Service time can only be changed by Super Admin, Practice Admin, or Provider.</span>
                                )}
                            </div>
                            <div className="follow-up-edit-field">
                                <label>Date</label>
                                <AppDateInput
                                    className="form-input-fl"
                                    value={editForm.date}
                                    onChange={(v) => setEditForm((prev) => ({ ...prev, date: v }))}
                                />
                            </div>
                            <div className="follow-up-edit-field">
                                <label>Time</label>
                                <input
                                    className="form-input-fl"
                                    type="time"
                                    value={editForm.time}
                                    onChange={(e) => setEditForm((prev) => ({ ...prev, time: e.target.value }))}
                                />
                            </div>
                        </div>
                        <div className="follow-up-edit-field">
                            <label>Follow Up Note</label>
                            <textarea
                                value={editForm.content}
                                onChange={(e) => setEditForm((prev) => ({ ...prev, content: e.target.value }))}
                                rows={5}
                            />
                        </div>
                        <div className="follow-up-edit-actions">
                            <button className="btn-fl" onClick={() => setEditingFollowUp(null)}>Cancel</button>
                            <button className="btn-fl" onClick={handleSaveEdit}>Save</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FollowUpList;
